import type { SimStock, SimulationState } from "@/hooks/useSimulator";

interface Props {
  stocks: SimStock[];
  simulation: SimulationState;
}

const SECTOR_ICONS: Record<string, string> = {
  Technology: "💻",
  Energy: "⚡",
  Consumer: "🛒",
  Finance: "🏦",
  Industrial: "🏭",
};

const SectorHeatmap = ({ stocks, simulation }: Props) => {
  const prevPrices =
    simulation.price_history.length >= 2
      ? simulation.price_history[simulation.price_history.length - 2]?.prices || {}
      : simulation.price_history[0]?.prices || {};

  const sectorData = stocks.reduce<
    Record<string, { totalChange: number; count: number; tickers: { ticker: string; change: number; price: number }[] }>
  >((acc, stock) => {
    const sector = stock.sector;
    const price = simulation.stock_prices[stock.ticker] || stock.current_price;
    const prev = prevPrices[stock.ticker] || price;
    const change = prev ? ((price - prev) / prev) * 100 : 0;

    if (!acc[sector]) acc[sector] = { totalChange: 0, count: 0, tickers: [] };
    acc[sector].totalChange += change;
    acc[sector].count += 1;
    acc[sector].tickers.push({ ticker: stock.ticker, change, price });
    return acc;
  }, {});

  const sectors = Object.entries(sectorData)
    .map(([name, data]) => ({
      name,
      avgChange: data.totalChange / data.count,
      tickers: data.tickers.sort((a, b) => b.change - a.change),
    }))
    .sort((a, b) => b.avgChange - a.avgChange);

  const maxAbs = Math.max(...sectors.map((s) => Math.abs(s.avgChange)), 1);

  function getHeatColor(change: number): string {
    const intensity = Math.min(Math.abs(change) / maxAbs, 1);
    if (change >= 0) {
      return `rgba(52, 211, 153, ${0.1 + intensity * 0.5})`;
    }
    return `rgba(248, 113, 113, ${0.1 + intensity * 0.5})`;
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-heading font-bold text-sm text-foreground/90 mb-1">Sector Heatmap</h3>
        <p className="text-muted-foreground text-[10px]">Performance by sector this round</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {sectors.map((sector) => (
          <div
            key={sector.name}
            className="rounded-xl border border-border/50 p-3 transition-all hover:border-border"
            style={{ background: getHeatColor(sector.avgChange) }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-heading font-bold text-xs text-foreground/90">
                {SECTOR_ICONS[sector.name] || "📈"} {sector.name}
              </span>
              <span
                className={`font-heading font-bold text-sm ${
                  sector.avgChange >= 0 ? "text-emerald-400" : "text-red-400"
                }`}
              >
                {sector.avgChange >= 0 ? "+" : ""}
                {sector.avgChange.toFixed(2)}%
              </span>
            </div>
            <div className="space-y-0.5">
              {sector.tickers.map((t) => (
                <div key={t.ticker} className="flex items-center justify-between text-[10px]">
                  <span className="text-muted-foreground">{t.ticker}</span>
                  <span className={t.change >= 0 ? "text-emerald-400/80" : "text-red-400/80"}>
                    {t.change >= 0 ? "+" : ""}
                    {t.change.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Market breadth */}
      {(() => {
        const allStocks = stocks.map((s) => {
          const price = simulation.stock_prices[s.ticker] || s.current_price;
          const prev = prevPrices[s.ticker] || price;
          return ((price - prev) / prev) * 100;
        });
        const advancers = allStocks.filter((c) => c > 0).length;
        const decliners = allStocks.filter((c) => c < 0).length;
        const unchanged = allStocks.filter((c) => c === 0).length;
        const breadth = stocks.length > 0 ? (advancers / stocks.length) * 100 : 50;

        return (
          <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
            <p className="text-muted-foreground text-[10px] mb-2">Market Breadth</p>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="flex-1 h-2 rounded-full bg-muted/50 overflow-hidden flex">
                <div
                  className="h-full bg-emerald-400/60 transition-all"
                  style={{ width: `${breadth}%` }}
                />
                <div
                  className="h-full bg-red-400/60 transition-all"
                  style={{ width: `${100 - breadth}%` }}
                />
              </div>
            </div>
            <div className="flex justify-between text-[10px]">
              <span className="text-emerald-400/80">↑ {advancers} advancing</span>
              {unchanged > 0 && <span className="text-muted-foreground/50">{unchanged} flat</span>}
              <span className="text-red-400/80">↓ {decliners} declining</span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default SectorHeatmap;
